import React, { useState, useEffect } from 'react';
import { Button, Badge, Spinner, Alert, Form } from 'react-bootstrap';
import { 
  getUserOffers,
  userListEscrows,
  type Offer 
} from '../../services/p2pAPI';
import './OfferManagement.css';

interface Escrow {
  id: string;
  tradeId: string;
  buyerEmail: string;
  sellerEmail: string;
  amount: number;
  price: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

const EscrowsList: React.FC = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [selectedOfferId, setSelectedOfferId] = useState<string>('');
  const [escrows, setEscrows] = useState<Escrow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingEscrows, setLoadingEscrows] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadOffers();
  }, []);

  useEffect(() => {
    if (selectedOfferId) {
      loadEscrows(selectedOfferId);
    } else {
      setEscrows([]);
    }
  }, [selectedOfferId]);

  const loadOffers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await getUserOffers({
        status: 'active',
        limit: 100,
        offset: 0
      });
      
      setOffers(response.data || []);
    } catch (error: any) {
      console.error('Failed to load offers:', error);
      setError(error?.message || 'Failed to load offers');
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadEscrows = async (offerId: string) => {
    try {
      setLoadingEscrows(true);
      setError('');

      const response = await userListEscrows(offerId);
      
      // Handle different response structures
      const escrowsData = response.data || response || [];
      setEscrows(Array.isArray(escrowsData) ? escrowsData : []);
    } catch (error: any) {
      console.error('Failed to load escrows:', error);
      setError(error?.response?.data?.message || error?.message || 'Failed to load escrows');
      setEscrows([]);
    } finally {
      setLoadingEscrows(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { variant: string; text: string } } = {
      pending: { variant: 'warning', text: 'Pending' },
      paid: { variant: 'info', text: 'Paid' },
      crypto_sent: { variant: 'primary', text: 'Crypto Sent' },
      completed: { variant: 'success', text: 'Completed' },
      cancelled: { variant: 'danger', text: 'Cancelled' },
      disputed: { variant: 'danger', text: 'Disputed' },
    };

    const config = statusConfig[status.toLowerCase()] || { 
      variant: 'secondary', 
      text: status 
    };

    return <Badge bg={config.variant as any}>{config.text}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="trades-loading-state">
        <Spinner animation="border" role="status" />
        <p>Loading offers...</p>
      </div>
    );
  }

  return (
    <div className="escrows-list-container">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}

      {/* Offer Selection */}
      <div className="binance-results-section mb-4">
        <div className="results-header">
          <div className="results-info">
            <span className="results-type">Select Offer to View Escrows</span>
          </div>
        </div>
        <div className="p-3">
          <Form.Group>
            <Form.Label>Choose an Offer</Form.Label>
            <Form.Select
              value={selectedOfferId}
              onChange={(e) => setSelectedOfferId(e.target.value)}
              className="form-select-custom"
            >
              <option value="">Select an offer...</option>
              {offers.map((offer) => (
                <option key={offer.id} value={offer.id}>
                  {offer.type.toUpperCase()} {offer.cryptoType} - {offer.amount} @ {formatCurrency(offer.price)} (ID: {offer.id.slice(0, 8)}...)
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
      </div>

      {/* Escrows List */}
      {selectedOfferId ? (
        loadingEscrows ? (
          <div className="trades-loading-state">
            <Spinner animation="border" role="status" />
            <p>Loading escrows...</p>
          </div>
        ) : escrows.length === 0 ? (
          <div className="empty-state-modern">
            <div className="empty-state-icon">
              <i className="fas fa-users"></i>
            </div>
            <h4>No Escrows Found</h4>
            <p className="empty-state-text">
              No users have joined this offer yet. Share your offer to attract traders.
            </p>
          </div>
        ) : (
          <div className="binance-results-section">
            <div className="results-header">
              <div className="results-info">
                <span className="results-type">
                  Users Who Joined This Offer ({escrows.length})
                </span>
              </div>
            </div>

            <div className="binance-trades-table">
              <div className="trades-table-header">
                <div className="col-trade-id">Trade ID</div>
                <div className="col-crypto">Buyer</div>
                <div className="col-crypto">Seller</div>
                <div className="col-amount">Amount</div>
                <div className="col-price">Price</div>
                <div className="col-total">Total Value</div>
                <div className="col-status">Status</div>
                <div className="col-date">Created</div>
              </div>

              <div className="trades-table-body">
                {escrows.map((escrow) => (
                  <div key={escrow.id || escrow.tradeId} className="trade-table-row">
                    <div className="col-trade-id">
                      <div className="trade-id-value">
                        <i className="fas fa-hashtag"></i>
                        <code>{(escrow.id || escrow.tradeId || '').slice(0, 10)}...</code>
                      </div>
                    </div>
                    <div className="col-crypto">
                      <div className="trade-amount-value">
                        {escrow.buyerEmail || 'N/A'}
                      </div>
                    </div>
                    <div className="col-crypto">
                      <div className="trade-amount-value">
                        {escrow.sellerEmail || 'N/A'}
                      </div>
                    </div>
                    <div className="col-amount">
                      <div className="trade-amount-value">
                        {escrow.amount || 'N/A'}
                      </div>
                    </div>
                    <div className="col-price">
                      <div className="trade-price-value">
                        {escrow.price ? formatCurrency(escrow.price) : 'N/A'}
                      </div>
                    </div>
                    <div className="col-total">
                      <div className="trade-total-row-value">
                        {escrow.amount && escrow.price ? formatCurrency(escrow.amount * escrow.price) : 'N/A'}
                      </div>
                    </div>
                    <div className="col-status">
                      {getStatusBadge(escrow.status || 'pending')}
                    </div>
                    <div className="col-date">
                      <div className="trade-date-value">
                        {escrow.createdAt ? formatDate(escrow.createdAt) : 'N/A'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      ) : (
        <div className="empty-state-modern">
          <div className="empty-state-icon">
            <i className="fas fa-hand-pointer"></i>
          </div>
          <h4>Select an Offer</h4>
          <p className="empty-state-text">
            Please select an offer from the dropdown above to view users who have joined it.
          </p>
        </div>
      )}
    </div>
  );
};

export default EscrowsList;

